import React, { memo, useState } from 'react';
import UploadList from 'antd/es/upload/UploadList';
import { getVariantSession, LOGIN_TOKEN } from '@Variant';
import { generateUrl } from '@util';
import './picturWall.css';
import { Modal, Upload } from 'antd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import _ from 'lodash';
import { imagePreview } from '../util/pictureUtil';

const grid = 8;
const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? 'lightblue' : '#f9f9f9',
  display: 'flex',
  flexWrap: 'wrap',
  padding: grid * 2,
  overflow: 'auto',
});

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  padding: 0,
  margin: 4,
  height: 154,
  width: 154,
  background: isDragging ? 'lightgreen' : '#f9f9f9',
  ...draggableStyle,
});
const imageType = ['.png', '.jpg', '.jpeg'];

// type Props = {
//   onChange: (params: { fileList }) => void
// } & UploadProps
const PicturesWall = memo(({ onChange: onFileChange, action, ...props }) => {
  const [previewImage, setPreviewImage] = useState('');
  const [fileListCount, setFileListCount] = useState(0);
  const fileList = props.fileList || [];
  const { data, searchParams, urlParams, replaceParams, api } = action,
    {
      count = 10,
      imageWidth = 99999,
      imageHeight = 99999,
      imageSize = 500,
      locale = { previewFile: '预览图片', removeFile: '删除图片' },
    } = props;

  const url = _.isObject(action)
    ? generateUrl(api, searchParams, urlParams, replaceParams)
    : action;
  let beforeUploadCounter = 0; // 上传的次数，可以统计到多少个文件
  let allFilesLength = 0; //上传文件个数+当前文件列表个数
  let continueUpload = true; //是否继续上传

  // 上传图片尺寸限制
  const checkImageWH = options => {
    // 参数分别是上传的file，想要限制的宽，想要限制的高
    const { file, width, height, size } = options;

    return new Promise(function (resolve, reject) {
      const filereader = new FileReader();
      filereader.readAsDataURL(file);
      return (filereader.onload = e => {
        const src = e.target.result;
        const image = new Image();
        image.onload = function () {
          // eslint-disable-next-line react/no-this-in-sfc
          if (this.width > width || this.height > height || file.size >= size) {
            // 上传图片的宽高与传递过来的限制宽高作比较，超过限制则调用失败回调
            return reject();
          } else {
            return resolve();
          }
        };
        image.onerror = reject;
        image.src = src;
      });
    });
  };

  const beforeUpload = async file => {
    const options = {
      file,
      width: imageWidth,
      height: imageHeight,
      size: 1024 * imageSize, // 限制文件大小 1kb  == 1024b
    };
    continueUpload = true;
    beforeUploadCounter++;
    allFilesLength = fileListCount;
    continueUpload = allFilesLength <= count;

    if (allFilesLength > count) {
      Modal.error({
        title: `上传的文件不能超过${count}个，请重新选择`,
      });
      return false;
    }

    return checkImageWH(options).catch(res => {
      Modal.error({
        title: `上传图片大小不能超过${imageSize}kb`,
      });
      continueUpload = false;
      return false;
    });
  };

  const onChange = ({ fileList }) => {
    const maxCount = count || 10;
    fileList = fileList.slice(0, maxCount);
    fileList = fileList.filter(ele => !(ele.size && ele.size > 1024 * imageSize));
    beforeUploadCounter = 0;
    if (!continueUpload) {
      return false;
    }
    setFileListCount(fileList.length);
    onFileChange({ fileList });
  };

  const onRemove = file => {
    if (props.disabled) return Modal.warning({ title: `图片不支持删除` });

    const newFileList = fileList.filter(item => item.uid !== file.uid);
    onFileChange({ fileList: newFileList });
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return;
    }

    const newFileList = reorder(fileList, source.index, destination.index);
    onFileChange({ fileList: newFileList });
  };

  const onPreview = async file => {
    await imagePreview(file, ({ image }) => {
      setPreviewImage(image);
    });
  };

  const uploadParams = {
    action: url,
    data,
    name: 'files',
    accept: imageType && imageType.join(','),
    headers: {
      'login-token': getVariantSession(LOGIN_TOKEN),
      'X-Requested-With': null,
    },
    showUploadList: false,
    onChange: onChange,
    beforeUpload: beforeUpload,
    style: getItemStyle(),
  };

  return (
    <div>
      {fileList && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable" direction="horizontal">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="DragUploadContainer"
                style={getListStyle(snapshot.isDraggingOver)}>
                {fileList.map((item, index) => (
                  <Draggable key={item.uid} draggableId={item.uid} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        className="DragUpload">
                        <UploadList
                          locale={locale}
                          showDownloadIcon={false}
                          listType={props.listType}
                          onPreview={onPreview}
                          onRemove={onRemove}
                          items={[item]}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                <Upload {...uploadParams} {...props}>
                  {props.children}
                </Upload>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <Modal
        visible={!!previewImage}
        footer={null}
        onCancel={() => setPreviewImage('')}
        bodyStyle={{ padding: 0 }}>
        <img style={{ width: '100%' }} alt="" src={previewImage} />
      </Modal>
    </div>
  );
});

export default PicturesWall;
